import { previewActivity } from '../../features/preview/previewActivity';
// The article owns one scheduler. Scroll events never rerender the HTML tree.
export const MAX_FEED_FRAMES = 3;
export function createMediaViewport() {
  const entries = new Map();
  let timer;
  let running = false;
  let movingUntil = 0;
  let observer;
  let unsubscribe;
  const publish = (entry, active) => {
    if (entry.active === active) return;
    entry.active = active;
    entry.listeners.forEach((listener) => listener());
  };
  const schedule = (delay = 120) => {
    if (!running || timer != null) return;
    timer = setTimeout(run, delay);
  };
  function run() {
    timer = undefined;
    if (!running) return;
    if (document.hidden || previewActivity.getSnapshot()) {
      entries.forEach((entry) => publish(entry, false));
      return;
    }
    const height = window.innerHeight;
    const candidates = [...entries.values()]
      .map((entry) => ({ entry, rect: entry.node.getBoundingClientRect() }))
      .filter(
        ({ entry, rect }) =>
          rect.height > 0 &&
          rect.bottom >= -(entry.active ? 320 : 180) &&
          rect.top <= height + (entry.active ? 320 : 180),
      )
      .sort(
        (a, b) =>
          Math.abs(a.rect.top + a.rect.height / 2 - height / 2) -
          Math.abs(b.rect.top + b.rect.height / 2 - height / 2),
      )
      .slice(0, MAX_FEED_FRAMES)
      .map(({ entry }) => entry);
    entries.forEach((entry) => {
      if (!candidates.includes(entry)) publish(entry, false);
    });
    let started = false;
    let pending = false;
    candidates.forEach((entry) => {
      if (entry.active) return;
      if (started || Date.now() < movingUntil) {
        pending = true;
        return;
      }
      publish(entry, true);
      started = true;
    });
    if (pending) schedule();
  }
  const scroll = () => {
    movingUntil = Date.now() + 160;
    schedule();
  };
  const resize = () => schedule();
  const visibility = () => {
    if (document.hidden || previewActivity.getSnapshot()) {
      clearTimeout(timer);
      timer = undefined;
      entries.forEach((entry) => publish(entry, false));
    } else schedule(300);
  };
  return {
    start(root) {
      running = true;
      unsubscribe = previewActivity.subscribe(visibility);
      window.addEventListener('scroll', scroll, { passive: true, capture: true });
      window.addEventListener('resize', resize);
      document.addEventListener('visibilitychange', visibility);
      if (typeof ResizeObserver !== 'undefined') {
        observer = new ResizeObserver(resize);
        observer.observe(root);
      }
      schedule(300);
      return () => {
        running = false;
        clearTimeout(timer);
        timer = undefined;
        window.removeEventListener('scroll', scroll, true);
        window.removeEventListener('resize', resize);
        document.removeEventListener('visibilitychange', visibility);
        observer?.disconnect();
        unsubscribe?.();
        entries.forEach((entry) => publish(entry, false));
      };
    },
    register(id, node) {
      const previous = entries.get(id);
      entries.set(id, { node, active: false, listeners: previous?.listeners || new Set() });
      schedule();
      return () => {
        entries.delete(id);
        schedule();
      };
    },
    active(id) {
      return entries.get(id)?.active || false;
    },
    subscribe(id, listener) {
      const entry = entries.get(id);
      if (!entry) return () => {};
      entry.listeners.add(listener);
      return () => entry.listeners.delete(listener);
    },
  };
}
